/**
 * Plain-English vocabulary for `comms_inbound.link_state` and
 * `unlinked_reason`, shared by the Inbox and Pipeline pages so both describe
 * linking the same way.
 *
 * Two separate ideas, kept apart on every screen:
 *   Comms management  which client and claim the email is about (the link).
 *   Letter linking    which of our letters, by type, it answers (optional).
 */

export const LINK_STATE_LABEL: Record<string, string> = {
    linked: 'Linked to claim',
    candidates: 'Possible matches',
    unlinked: 'Not linked',
    not_applicable: 'No claim expected',
}

export const LINK_STATE_MEANING: Record<string, string> = {
    linked: 'We know which client and claim this email is about. It may or may not be tied to one of our letters.',
    candidates: 'The details fit more than one claim. A person picks the right one in the mailbox.',
    unlinked: 'Could not be tied to any client or claim yet. Reconcile keeps retrying as new client snapshots and lender references arrive; what remains is linked by hand.',
    not_applicable: 'Bounces, auto-replies, newsletters and other noise that quote no claim. Deliberately left without one.',
}

/** Label for a reason code; the empty reason means arrival matching found nothing and extraction has not run. */
export const unlinkedReasonLabel = (reason: string | null | undefined) => (reason ? reason.replace(/_/g, ' ') : 'not yet read')

export const linkStateLabel = (state: string | null | undefined) => (state ? LINK_STATE_LABEL[state] ?? state.replace(/_/g, ' ') : 'Unknown')

/** One sentence for why an email is not linked. Handles the `noise:x` / `rule:x` / `llm:x` prefixes. */
export const describeUnlinkedReason = (reason: string | null | undefined): string => {
    if (!reason) return 'Nothing in the email matched on arrival (no reply header, reply address or reference of ours) and it has not been extracted yet. It may still link once read.'
    const [prefix, rest] = reason.split(':', 2)
    if (rest !== undefined) {
        const what = rest.replace(/_/g, ' ')
        if (prefix === 'noise') return `Filtered as noise (${what}) and quotes no claim of ours; no claim expected.`
        if (prefix === 'rule') return `A rule filed it as "${what}"; no claim expected.`
        if (prefix === 'llm') return `Categorised as "${what}", which needs no claim.`
    }
    switch (reason) {
        case 'no_identifiers':
            return 'The email contains none of our references or recognisable client details.'
        case 'identifiers_not_found':
            return 'The email quotes references or client details, but none match a claim we hold.'
        case 'ambiguous_identity':
            return 'The details in the email fit more than one claim.'
        case 'manual_unlink':
            return 'A team member removed the previous link.'
        default:
            return reason.replace(/_/g, ' ')
    }
}

/** How linking happens, for explanatory copy. */
export const LINKING_EXPLAINER =
    'Linking ties an email to a client and claim. It runs twice: on arrival, using the cheap signals (reply headers, the case reply address, our claim, footer or tracking reference in the text), and again after extraction, using the client details the email quotes (agreement number, name, postcode, lender reference) scored against the client snapshots. A claim we know links even before any letter has gone out. Separately, letter linking records which of our letters (by type: letter of complaint, chaser, settlement response…) the email answers; that is chosen automatically only when the email replies to a letter directly or the claim has one obvious letter, and can always be changed by a person.'

/** Letter linking, in one sentence, for card subtitles and help text. */
export const LETTER_LINKING_EXPLAINER =
    'We send several letters of different types on a claim. Letter linking records which one an email answers, so replies can be reported by letter type as well as by category. It never changes which client or claim the email belongs to.'
